import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth, gradeAtLeast } from "../lib/auth.jsx";
import { useI18n } from "../lib/i18n.jsx";
import { Heart, Users, Plus, Crown, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

export default function PantheonListPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [gods, setGods] = useState([]);
  const [loading, setLoading] = useState(true);
  const canEnshrine = gradeAtLeast(user, "Neophyte");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/gods");
      setGods(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <main data-testid="pantheon-list-page" className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <header className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <p className="label-small text-[#8B3A3A] mb-3">{t("pantheon.eyebrow")}</p>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tight text-[#1A1918]">{t("pantheon.heading")}</h1>
          <p className="text-[#4A4238] mt-3 max-w-2xl">{t("pantheon.intro")}</p>
        </div>
        {canEnshrine ? (
          <Link to="/pantheon/new" data-testid="pantheon-enshrine-btn" className="btn-primary inline-flex items-center gap-2 self-start">
            <Plus size={14} /> {t("pantheon.enshrine_btn")}
          </Link>
        ) : (
          <p className="text-xs italic text-[#7A7061] max-w-xs text-right" data-testid="pantheon-cant-enshrine">{t("pantheon.cant_enshrine")}</p>
        )}
      </header>

      {loading ? (
        <p className="text-[#7A7061] italic">{t("common.loading")}</p>
      ) : gods.length === 0 ? (
        <div className="border border-dashed border-[#C1B49A] p-16 text-center">
          <p className="font-display italic text-3xl text-[#4A4238]">{t("pantheon.empty_title")}</p>
          <p className="text-sm text-[#7A7061] mt-2">{t("pantheon.empty_sub")}</p>
        </div>
      ) : (
        <ol className="border border-[#C1B49A]" data-testid="pantheon-list">
          {gods.map((g, idx) => {
            const isLast = idx === gods.length - 1;
            return (
              <li key={g.id} data-testid={`pantheon-row-${g.id}`}>
                <Link
                  to={`/pantheon/${g.id}`}
                  className="grid grid-cols-12 gap-4 md:gap-6 p-5 md:p-7 items-center bg-[#EAE0CA]/40 hover:bg-[#EAE0CA]/80 transition-colors"
                  style={{ borderBottom: isLast ? "0" : "1px solid #C1B49A" }}
                >
                  <div className="col-span-2 md:col-span-1 font-display text-3xl md:text-4xl text-[#8B3A3A] italic flex items-center gap-2">
                    {idx + 1}
                    {isLast && <Crown size={18} className="text-[#B8860B]" />}
                  </div>
                  <div className="col-span-3 md:col-span-2">
                    <div
                      className="w-full aspect-square border border-[#C1B49A] bg-[#F4EEDF]"
                      style={{
                        backgroundImage: g.image_data ? `url(${g.image_data})` : undefined,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                      aria-label={g.name}
                    />
                  </div>
                  <div className="col-span-7 md:col-span-7">
                    <h3 className="font-display text-2xl md:text-3xl text-[#1A1918] leading-tight">{g.name}</h3>
                    <p className="text-xs text-[#7A7061] font-mono tracking-widest uppercase mt-1">
                      {t("pantheon.enshrined_by")} <span className="text-[#4A4238]">{g.creator_name || "—"}</span>
                    </p>
                  </div>
                  <div className="col-span-12 md:col-span-2 flex md:flex-col items-center md:items-end gap-4 md:gap-1 text-sm">
                    <span className="inline-flex items-center gap-1.5 text-[#8B3A3A]" data-testid={`pantheon-likes-${g.id}`}>
                      <Heart size={14} fill={g.user_has_liked ? "#8B3A3A" : "none"} /> {g.likes_count}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-[#4A4238]" data-testid={`pantheon-worshippers-${g.id}`}>
                      <Users size={14} /> {g.worshippers_count}
                    </span>
                    <ArrowUpRight size={16} className="hidden md:block text-[#7A7061]" />
                  </div>
                </Link>
              </li>
            );
          })}
        </ol>
      )}
    </main>
  );
}
